// Production builds can also be served on staging or locally.
export const isProductionTrackingHost = () =>
  typeof window !== 'undefined' &&
  /^(rxrooster\.com|www\.rxrooster\.com|app\.rxrooster\.com)$/.test(
    window.location.hostname.toLowerCase(),
  );

// react-router decodes each path segment before it matches a route, so
// /%61dmin/users renders the admin console. Every check below reads the path
// the same way: decode per segment, keep a segment that is not a valid escape
// as written, then lowercase. Decode once only, as the router does.
// Mirrors normalizeTrackingPath in the GTM guard in public/index.html.
export const normalizeTrackingPath = (pathname) =>
  String(pathname || '')
    .split('/')
    .map((segment) => {
      try {
        return decodeURIComponent(segment);
      } catch (e) {
        return segment;
      }
    })
    .join('/')
    .toLowerCase();

// Internal console areas: the admin console and the marketing console. Their
// URLs carry personal identifiers (provider, company and user ids, typed place
// searches), and GTM relays every page view with the full URL to Meta and
// Google. Matches the root itself and anything under it, case-insensitively,
// and nothing that merely shares the prefix (/administrators, /marketing-tips).
// Mirrors isConsolePath in the GTM guard in public/index.html.
const INTERNAL_CONSOLE_ROOTS = ['/admin', '/marketing'];

const matchesConsoleRoot = (normalizedPath) =>
  INTERNAL_CONSOLE_ROOTS.some(
    (root) => normalizedPath === root || normalizedPath.startsWith(`${root}/`),
  );

export const isInternalConsolePath = (pathname) =>
  matchesConsoleRoot(normalizeTrackingPath(pathname));

// These routes contain short-lived bearer credentials in the path or hash.
// External analytics must not initialize on them or receive events from them:
// page_location and referrer fields would otherwise disclose the credential.
// Internal console pages are blocked too (see isInternalConsolePath).
//
// A credential in the QUERY string is not handled here. The GTM bootstrap moves
// those parameters into a same-tab stash and rewrites the URL before GTM loads
// (public/index.html, src/utils/urlStash.js), so the sign-in landings and /join
// are tracked on their clean URL. A path cannot be rewritten that way, hence the
// path blocks below.
const TRACKING_BLOCKED_EXACT_PATHS = new Set([
  '/company/coverage-integrations',
  '/coverage/continue',
  '/mcp/oauth/callback',
  '/mcp-publication',
  '/company/mcp-publication',
]);

const TRACKING_BLOCKED_PATH_PREFIXES = [
  '/partners/',
  '/welcome/',
  // Legacy welcome links use /login/:token.
  '/login/',
  // Recruiter match decks, listing verify links and listing claim links.
  // Mirrors the GTM guard in public/index.html. The bare /verify code door
  // carries no token and stays trackable.
  '/rm/',
  '/verify/',
  '/claim-listing/',
  // More routes whose path IS the credential: the provider match deck (twin of
  // /rm/), the legacy listing claim redirect, guest negotiation invites, publish
  // and rate links, report links, SMS consent, unsubscribe and secure downloads.
  // /feed-claim/<token> and /i/<code> are not here: the bootstrap rewrites them
  // to a neutral path and stashes the code, so those funnels stay measured.
  // Mirrors isCredentialPath in public/index.html.
  '/pm/',
  '/claim/',
  '/negotiate/invite/',
  '/publish/',
  '/rate/',
  '/listing-health-report/',
  '/sms-confirm/',
  '/listing-report/',
  '/recruiter-report/',
  '/interview-feedback/',
  '/unsubscribe/',
  '/dl/',
];

// Console pages open tracked routes in a new tab (a negotiation, a services
// page). These flags, equal to 1, mark the visit as an admin's own, so the new
// document stays out of analytics too. The flag controls client telemetry only:
// the API still requires a valid ADMIN session for anything it gates on it.
const CONSOLE_FLAG_QUERY_KEYS = ['adminPreview', 'adminReview', 'adminOutreach'];

const readQuery = (search) => {
  try {
    return new URLSearchParams(search || '');
  } catch (e) {
    return new URLSearchParams('');
  }
};

const hasConsoleFlag = (params) =>
  CONSOLE_FLAG_QUERY_KEYS.some((key) => params.get(key) === '1');

// Whether a query string ('?a=1' or 'a=1') carries one of the console flags
// above, equal to 1. RoleBasedRoute uses it to keep such a URL out of ?next=.
export const hasConsoleFlagQuery = (search) => hasConsoleFlag(readQuery(search));

// A path whose segments ARE the credential (normalized: decoded, lowercased).
// A trailing slash matches the same route.
const isCredentialTrackingPath = (normalizedPath) => {
  const exactPath = normalizedPath.length > 1 && normalizedPath.endsWith('/')
    ? normalizedPath.slice(0, -1)
    : normalizedPath;
  return (
    TRACKING_BLOCKED_EXACT_PATHS.has(exactPath) ||
    TRACKING_BLOCKED_PATH_PREFIXES.some((prefix) => normalizedPath.startsWith(prefix))
  );
};

// A URL that must not reach analytics: a console page, a credential path, or a
// console flag in its query.
const isPrivateTarget = (pathname, search) => {
  const normalizedPath = normalizeTrackingPath(pathname);
  return (
    matchesConsoleRoot(normalizedPath) ||
    isCredentialTrackingPath(normalizedPath) ||
    hasConsoleFlag(readQuery(search))
  );
};

// /login?next=<URL>: the URL GTM sees holds the next target too. The login
// wall used to put every attempted URL there, console pages and admin-flag
// links included, and RoleBasedRoute still does for other routes (a credential
// path among them). Those links also live on in bookmarks and browser history,
// so a /login whose next is private is private itself. The value is read once
// here (by URLSearchParams) and once by the path normalizer, which is the
// router's own two steps. An absolute next is read through URL as well.
const isLoginNextPrivate = (normalizedPath, params) => {
  if (normalizedPath !== '/login' && normalizedPath !== '/login/') return false;
  const next = String(params.get('next') || '');
  if (!next) return false;
  const withoutHash = next.split('#')[0];
  const queryAt = withoutHash.indexOf('?');
  if (
    isPrivateTarget(
      queryAt === -1 ? withoutHash : withoutHash.slice(0, queryAt),
      queryAt === -1 ? '' : withoutHash.slice(queryAt),
    )
  ) {
    return true;
  }
  try {
    const target = new URL(next, 'https://app.rxrooster.com');
    return isPrivateTarget(target.pathname, target.search);
  } catch (e) {
    return false;
  }
};

export const isExternalTrackingBlockedPath = (pathname, search) => {
  const currentPath = pathname === undefined && typeof window !== 'undefined'
    ? window.location.pathname
    : pathname;
  const normalizedPath = normalizeTrackingPath(currentPath);
  const currentSearch = search === undefined && typeof window !== 'undefined'
    ? window.location.search
    : search;
  const params = readQuery(currentSearch);

  return (
    matchesConsoleRoot(normalizedPath) ||
    isCredentialTrackingPath(normalizedPath) ||
    hasConsoleFlag(params) ||
    isLoginNextPrivate(normalizedPath, params)
  );
};

// True when the GTM bootstrap marked this document as one that started on a
// console page (or a link out of one). Such a document never arms tracking, even
// after an in-app move to a public page, because its first URL was private.
export const isConsoleDocument = () =>
  typeof window !== 'undefined' && window.__rxrConsoleDocument === true;

// window.gtag in production. It pushes onto GTM's own dataLayer, never a
// separate one (GTM's Google tag owns the Ads conversion ID there), and must
// push the arguments object itself, which is what gtag.js reads. Calls made on
// a blocked route, or in a console-origin document, are dropped.
export function gtagToDataLayer() {
  if (isConsoleDocument() || isExternalTrackingBlockedPath()) return;
  window.dataLayer.push(arguments);
}
